import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User,
  Eye,
  EyeOff,
  Mail,
  Lock,
  Phone,
  MapPin,
  Map
} from 'lucide-react';
import { useTalentAuth } from '../../contexts/TalentAuthContext';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import Header from '../public/header';
import Footer from '../public/Footer';
import './UniversalRegister.css';

declare global {
  interface Window {
    google: any;
  }
}



const UniversalRegister: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    location: '',
    bio: '',
    parish: '',
    address: '',
    acceptTerms: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.0060 }); // Default to NYC
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [isMapsApiLoaded, setIsMapsApiLoaded] = useState(() => {
    return !!(window.google && window.google.maps && window.google.maps.places);
  });
  const [userType, setUserType] = useState<'talent' | 'customer'>(() => {
    const params = new URLSearchParams(window.location.search);
    const type = params.get('type');
    return type === 'customer' ? 'customer' : 'talent';
  });
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const locationInputRef = useRef<HTMLInputElement>(null);

  const { register: talentRegister, loginWithGoogle: talentLoginWithGoogle } = useTalentAuth();
  const { register: customerRegister } = useCustomerAuth();
  const navigate = useNavigate();

  // Initialize Google Maps
  const initializeMap = useCallback(() => {
    if (!mapRef.current || !window.google || !window.google.maps) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: mapCenter,
      zoom: 13,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    mapInstanceRef.current = map;

    // Add existing marker if there's a selected location
    if (selectedLocation) {
      const marker = new window.google.maps.Marker({
        position: selectedLocation,
        map,
        title: 'Your Selected Location',
        icon: {
          url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
          scaledSize: new window.google.maps.Size(32, 32),
        },
        draggable: true, // Make marker draggable
      });

      markerRef.current = marker;

      // Add drag listener to marker
      marker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();
          setSelectedLocation({ lat, lng });

          // Reverse geocode the new position
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results: any, status: any) => {
            if (status === 'OK' && results && results[0]) {
              const address = results[0].formatted_address;
              setFormData(prev => ({ ...prev, location: address }));
            }
          });
        }
      });
    }

    // Add click listener to map
    map.addListener('click', (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();

        setSelectedLocation({ lat, lng });

        // Remove existing marker
        if (markerRef.current) {
          markerRef.current.setMap(null);
        }

        // Add new marker with custom icon
        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          map,
          title: 'Your Selected Location',
          icon: {
            url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
            scaledSize: new window.google.maps.Size(32, 32),
          },
          draggable: true, // Make marker draggable
          animation: window.google.maps.Animation.DROP, // Add drop animation
        });

        markerRef.current = marker;

        // Add drag listener to marker
        marker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
          if (event.latLng) {
            const newLat = event.latLng.lat();
            const newLng = event.latLng.lng();
            setSelectedLocation({ lat: newLat, lng: newLng });

            // Reverse geocode the new position
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat: newLat, lng: newLng } }, (results: any, status: any) => {
              if (status === 'OK' && results && results[0]) {
                const address = results[0].formatted_address;
                setFormData(prev => ({ ...prev, location: address }));
              }
            });
          }
        });

        // Reverse geocode to get address
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results: any, status: any) => {
          if (status === 'OK' && results && results[0]) {
            const address = results[0].formatted_address;
            setFormData(prev => ({ ...prev, location: address }));
            // Clear location error if it exists
            if (errors.location) {
              setErrors(prev => ({ ...prev, location: '' }));
            }
          }
        });
      }
    });
  }, [mapCenter, errors.location, selectedLocation]);

  // Load Google Maps script
  useEffect(() => {
    if (!window.google || !window.google.maps) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setIsMapsApiLoaded(true);
        if (showMap) {
          initializeMap();
        }
      };
      document.head.appendChild(script);

      return () => {
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
      };
    } else {
      setIsMapsApiLoaded(true);
      if (showMap) {
        initializeMap();
      }
    }
  }, [showMap, initializeMap]);

  // Get user's current location
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setMapCenter({ lat, lng });
          setSelectedLocation({ lat, lng });

          // Update map center if map is already initialized
          if (mapInstanceRef.current) {
            mapInstanceRef.current.setCenter({ lat, lng });
            mapInstanceRef.current.setZoom(15); // Zoom in closer for current location

            // Remove existing marker
            if (markerRef.current) {
              markerRef.current.setMap(null);
            }

            // Add marker at current location with special icon
            const marker = new window.google.maps.Marker({
              position: { lat, lng },
              map: mapInstanceRef.current,
              title: 'Your Current Location',
              icon: {
                url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
                scaledSize: new window.google.maps.Size(32, 32),
              },
              draggable: true,
              animation: window.google.maps.Animation.BOUNCE, // Add bounce animation
            });

            // Stop bounce animation after 2 seconds
            setTimeout(() => {
              marker.setAnimation(null);
            }, 2000);

            markerRef.current = marker;

            // Add drag listener to marker
            marker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
              if (event.latLng) {
                const newLat = event.latLng.lat();
                const newLng = event.latLng.lng();
                setSelectedLocation({ lat: newLat, lng: newLng });

                // Reverse geocode the new position
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ location: { lat: newLat, lng: newLng } }, (results: any, status: any) => {
                  if (status === 'OK' && results && results[0]) {
                    const address = results[0].formatted_address;
                    setFormData(prev => ({ ...prev, location: address }));
                  }
                });
              }
            });

            // Reverse geocode to get address
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results: any, status: any) => {
              if (status === 'OK' && results && results[0]) {
                const address = results[0].formatted_address;
                setFormData(prev => ({ ...prev, location: address }));
                // Clear location error if it exists
                if (errors.location) {
                  setErrors(prev => ({ ...prev, location: '' }));
                }
              }
            });
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          setMessage({ type: 'error', text: 'Unable to get your current location. Please select manually on the map.' });
        }
      );
    } else {
      setMessage({ type: 'error', text: 'Geolocation is not supported by this browser.' });
    }
  };

  // Clear selected location and marker
  const clearLocation = () => {
    setSelectedLocation(null);
    setFormData(prev => ({ ...prev, location: '' }));

    // Remove marker from map
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
  };

  const parishes = [
    'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary',
    'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland',
    'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'
  ];

  // Initialize Google Places Autocomplete for inputs
  useEffect(() => {
    if (!isMapsApiLoaded || !window.google?.maps?.places) return;

    let autocomplete: google.maps.places.Autocomplete | null = null;

    if (userType === 'customer' && addressInputRef.current) {
      const autocompleteInstance = new window.google.maps.places.Autocomplete(addressInputRef.current, {
        types: ['geocode', 'establishment'],
      });
      autocomplete = autocompleteInstance;

      autocompleteInstance.addListener('place_changed', () => {
        const place = autocompleteInstance.getPlace();
        if (place && place.formatted_address) {
          setFormData(prev => ({ ...prev, address: place.formatted_address || '' }));
          
          // Try to extract parish if available
          if (place.address_components) {
            for (const component of place.address_components) {
              const parishMatch = parishes.find(
                p => component.long_name.includes(p) || p.includes(component.long_name)
              );
              if (parishMatch) {
                setFormData(prev => ({ ...prev, parish: parishMatch }));
                break;
              }
            }
          }
        }
      });
    } else if (userType === 'talent' && locationInputRef.current) {
      const autocompleteInstance = new window.google.maps.places.Autocomplete(locationInputRef.current, {
        types: ['geocode', 'establishment'],
      });
      autocomplete = autocompleteInstance;

      autocompleteInstance.addListener('place_changed', () => {
        const place = autocompleteInstance.getPlace();
        if (place && place.formatted_address) {
          const address = place.formatted_address;
          setFormData(prev => ({ ...prev, location: address }));

          // Update map position if geometry is available
          if (place.geometry && place.geometry.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            setMapCenter({ lat, lng });
            setSelectedLocation({ lat, lng });

            if (mapInstanceRef.current) {
              mapInstanceRef.current.setCenter({ lat, lng });
              mapInstanceRef.current.setZoom(15);

              if (markerRef.current) {
                markerRef.current.setMap(null);
              }

              const marker = new window.google.maps.Marker({
                position: { lat, lng },
                map: mapInstanceRef.current,
                title: 'Selected Location',
                draggable: true,
              });
              markerRef.current = marker;

              marker.addListener('dragend', (event: google.maps.MapMouseEvent) => {
                if (event.latLng) {
                  const newLat = event.latLng.lat();
                  const newLng = event.latLng.lng();
                  setSelectedLocation({ lat: newLat, lng: newLng });

                  const geocoder = new window.google.maps.Geocoder();
                  geocoder.geocode({ location: { lat: newLat, lng: newLng } }, (results: any, status: any) => {
                    if (status === 'OK' && results && results[0]) {
                      setFormData(prev => ({ ...prev, location: results[0].formatted_address }));
                    }
                  });
                }
              });
            }
          }
        }
      });
    }

    return () => {
      if (autocomplete) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, [userType, isMapsApiLoaded]);

  // Handle Google OAuth callback
  const handleGoogleCallback = useCallback(async (response: any) => {
    try {
      console.log('Google OAuth: Callback received:', response);
      console.log('Google OAuth: Credential length:', response.credential?.length);

      setIsGoogleLoading(true);
      setMessage({ type: '', text: '' });

      if (userType === 'talent') {
        // Try talent login for service providers
        console.log('Google OAuth: Attempting talent login...');
        const talentResult = await talentLoginWithGoogle(response.credential);

        if (talentResult.success) {
          setMessage({ type: 'success', text: 'Google registration successful! Redirecting...' });
          // navigate('/talent/membership/select');
          navigate('/talent/dashboard');
        } else {
          setMessage({ type: 'error', text: talentResult.message || 'Google registration failed. Please try again.' });
        }
      } else {
        // Google registration not supported for customers
        setMessage({ type: 'error', text: 'Google registration is only available for Service Providers. Please use email/password registration for Customer accounts.' });
      }
    } catch (error) {
      console.error('Google OAuth: Callback error:', error);
      setMessage({ type: 'error', text: 'Google registration failed. Please try again.' });
    } finally {
      setIsGoogleLoading(false);
    }
  }, [talentLoginWithGoogle, navigate, userType]);

  // Initialize Google OAuth
  useEffect(() => {
    const initializeGoogleOAuth = () => {


      if (window.google && window.google.accounts) {
        console.log('Google OAuth: Google accounts API available');
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || '877295110750-se0qnm049lgsn63pga699j5npljnc6oq.apps.googleusercontent.com',
          callback: handleGoogleCallback,
        });
        console.log('Google OAuth: Initialization complete');
      } else {
        console.error('Google OAuth: Google accounts API not available');
      }
    };

    // Load Google OAuth script
    console.log('Google OAuth: Loading script...');
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('Google OAuth: Script loaded successfully');
      initializeGoogleOAuth();
    };
    script.onerror = (error) => {
      console.error('Google OAuth: Script failed to load:', error);
    };
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [handleGoogleCallback]);

  // Handle Google sign-in button click
  const handleGoogleSignIn = () => {
    if (window.google && window.google.accounts) {
      window.google.accounts.id.prompt();
    } else {
      setMessage({ type: 'error', text: 'Google Sign-In is not available. Please try again later.' });
    }
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Clear message
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  // Handle checkbox changes
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (userType === 'talent') {
      if (!formData.location.trim()) {
        newErrors.location = 'Location is required for service providers';
      }
    }

    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      if (userType === 'talent') {
        // Talent registration
        const result = await talentRegister({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          address: formData.location,
          bio: formData.bio
        });

        if (result.success) {
          setMessage({
            type: 'success',
            text: 'Registration successful! Please select a membership plan...'
          });

          setTimeout(() => {
            // navigate('/talent/membership/select');
            navigate('/talent/dashboard');
          }, 2000);
        } else {
          setMessage({
            type: 'error',
            text: result.message || 'Registration failed. Please try again.'
          });
        }
      } else {
        // Customer registration
        const registerData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          phone: formData.phone || undefined,
          parish: formData.parish || undefined,
          address: formData.address || undefined,
        };

        const result = await customerRegister(registerData);
        if (result.success) {
          setMessage({ type: 'success', text: 'Registration successful! Redirecting...' });
          setTimeout(() => navigate('/customer/dashboard'), 1500);
        } else {
          setMessage({ type: 'error', text: result.message || 'Registration failed. Please try again.' });
        }
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.message || 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className='partner-login new-talent-css'>
        <div className='partner-login-box'>
          {/* User Type Selection */}
          <div className="header-section">
            <h3>Create Account</h3>
            <p>Choose your account type to get started</p>
          </div>

          <div className="user-type-selection">
            <div>
              <label className={userType === 'customer' ? 'selected' : ''}>
                <input
                  type="radio"
                  name="userType"
                  value="customer"
                  checked={userType === 'customer'}
                  onChange={(e) => setUserType(e.target.value as 'talent' | 'customer')}
                />
                <span>Customer</span>
              </label>

              <label className={userType === 'talent' ? 'selected' : ''}>
                <input
                  type="radio"
                  name="userType"
                  value="talent"
                  checked={userType === 'talent'}
                  onChange={(e) => setUserType(e.target.value as 'talent' | 'customer')}
                />
                <span>Service Provider</span>
              </label>
            </div>
          </div>

          {message.text && (
            <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName" className="form-label">First Name</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <User size={18} />
                  </div>
                  <input
                    type="text"
                    className={`form-control ${errors.firstName ? 'error' : ''}`}
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Enter your first name"
                  />
                </div>
                {errors.firstName && <div className="error-message">{errors.firstName}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="lastName" className="form-label">Last Name</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <User size={18} />
                  </div>
                  <input
                    type="text"
                    className={`form-control ${errors.lastName ? 'error' : ''}`}
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Enter your last name"
                  />
                </div>
                {errors.lastName && <div className="error-message">{errors.lastName}</div>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">Email Address</label>
              <div className="input-wrapper">
                <div className="input-icon">
                  <Mail size={18} />
                </div>
                <input
                  type="email"
                  className={`form-control ${errors.email ? 'error' : ''}`}
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && <div className="error-message">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="phone" className="form-label">Phone Number</label>
              <div className="input-wrapper">
                <div className="input-icon">
                  <Phone size={18} />
                </div>
                <input
                  type="tel"
                  className={`form-control ${errors.phone ? 'error' : ''}`}
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter your phone number"
                />
              </div>
              {errors.phone && <div className="error-message">{errors.phone}</div>}
            </div>

            {userType === 'customer' && (
              <>
                <div className="form-group">
                  <label htmlFor="parish" className="form-label">Parish</label>
                  <div className="input-wrapper">
                    <div className="input-icon">
                      <MapPin size={18} />
                    </div>
                    <select
                      className={`form-control ${errors.parish ? 'error' : ''}`}
                      id="parish"
                      name="parish"
                      value={formData.parish}
                      onChange={handleInputChange}
                    >
                      <option value="">Select a parish</option>
                      {parishes.map(parish => (
                        <option key={parish} value={parish}>{parish}</option>
                      ))}
                    </select>
                  </div>
                  {errors.parish && <div className="error-message">{errors.parish}</div>}
                </div>

                <div className="form-group">
                  <label htmlFor="address" className="form-label">Address</label>
                  <div className="input-wrapper">
                    <div className="input-icon">
                      <MapPin size={18} />
                    </div>
                    <input
                      ref={addressInputRef}
                      type="text"
                      className={`form-control ${errors.address ? 'error' : ''}`}
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Enter your address"
                    />
                  </div>
                  {errors.address && <div className="error-message">{errors.address}</div>}
                </div>
              </>
            )}

            {userType === 'talent' && (
              <>
                <div className="form-group">
                  <label htmlFor="location" className="form-label">Location</label>
                  <div className="input-wrapper">
                    <div className="input-icon">
                      <MapPin size={18} />
                    </div>
                    <input
                      ref={locationInputRef}
                      type="text"
                      className={`form-control ${errors.location ? 'error' : ''}`}
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="Enter your location or select on map below"
                    />
                  </div>
                  {errors.location && <div className="error-message">{errors.location}</div>}

                  {/* Map Controls */}
                  <div style={{ marginTop: '10px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="map-toggle-btn"
                      onClick={() => setShowMap(!showMap)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        border: '1px solid #e9ecef',
                        borderRadius: '6px',
                        background: showMap ? '#3498db' : 'white',
                        color: showMap ? 'white' : '#6b7280',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Map size={16} />
                      {showMap ? 'Hide Map' : 'Show Map'}
                    </button>

                    {showMap && (
                      <>
                        <button
                          type="button"
                          className="current-location-btn"
                          onClick={getCurrentLocation}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 16px',
                            border: '1px solid #e9ecef',
                            borderRadius: '6px',
                            background: 'white',
                            color: '#6b7280',
                            fontSize: '14px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <MapPin size={16} />
                          Use My Location
                        </button>

                        {selectedLocation && (
                          <button
                            type="button"
                            className="clear-location-btn"
                            onClick={clearLocation}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 16px',
                              border: '1px solid #e74c3c',
                              borderRadius: '6px',
                              background: 'white',
                              color: '#e74c3c',
                              fontSize: '14px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            ✕ Clear Pin
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  {/* Map Container */}
                  {showMap && (
                    <div style={{ marginTop: '15px' }}>
                      <div
                        ref={mapRef}
                        style={{
                          width: '100%',
                          height: '300px',
                          borderRadius: '8px',
                          border: '1px solid #e9ecef',
                          overflow: 'hidden'
                        }}
                      />
                      <div style={{
                        fontSize: '12px',
                        color: '#6b7280',
                        marginTop: '8px',
                        marginBottom: '0',
                        textAlign: 'center',
                        lineHeight: '1.4'
                      }}>
                        Click on the map to select your location or drag the marker to adjust
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="bio" className="form-label">About</label>
                  <textarea
                    className={`form-control ${errors.bio ? 'error' : ''}`}
                    id="bio"
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    placeholder="Tell us about yourself, your experience, and what makes you unique..."
                    rows={4}
                    style={{
                      padding: '12px 15px',
                      border: errors.bio ? '2px solid #e74c3c' : '2px solid #e9ecef',
                      borderRadius: '10px',
                      fontSize: '14px',
                      transition: 'all 0.3s ease',
                      background: '#f8f9fa',
                      resize: 'vertical',
                      minHeight: '80px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3498db';
                      e.target.style.background = 'white';
                      e.target.style.boxShadow = '0 0 0 3px rgba(52, 152, 219, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.bio ? '#e74c3c' : '#e9ecef';
                      e.target.style.background = '#f8f9fa';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  {errors.bio && <div className="error-message">{errors.bio}</div>}
                </div>
              </>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="password" className="form-label">Password</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <Lock size={18} />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className={`form-control ${errors.password ? 'error' : ''}`}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Create a password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && <div className="error-message">{errors.password}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <Lock size={18} />
                  </div>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className={`form-control ${errors.confirmPassword ? 'error' : ''}`}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.confirmPassword && <div className="error-message">{errors.confirmPassword}</div>}
              </div>
            </div>

            <div className="form-group">
              <label className="checkbox-label" style={{ display: 'flex' }}>
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleCheckboxChange}
                />
<span>I agree to{' '}
  <Link to="/terms" target="_blank" className="link-placeholder">
    Terms of Service
  </Link>{' '}
  and acknowledge{' '}
  <Link to="/privacy" target="_blank" className="link-placeholder">
    Privacy Policy
  </Link>.
</span>              </label>
              {errors.acceptTerms && <div className="error-message">{errors.acceptTerms}</div>}
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="button-spinner"></div>
                  <span>Creating account...</span>
                </>
              ) : (
                userType === 'talent' ? 'Register as Professional' : 'Register as Customer'
              )}
            </button>
          </form>

          {userType === 'talent' && (
            <div className='continue-google'>
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={isGoogleLoading}
                className={isGoogleLoading ? 'loading' : ''}
              >
                {isGoogleLoading ? (
                  <>
                    <div className="button-spinner"></div>
                    <span>Creating account with Google...</span>
                  </>
                ) : (
                  <>
                    <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="LgbsSe-Bz112c">
                      <g>
                        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                        <path fill="none" d="M0 0h48v48H0z"></path>
                      </g>
                    </svg>
                    Continue with Google
                  </>
                )}
              </button>
              <p>If you don't have a Skillseek account through Google, we'll create one for you.</p>
            </div>
          )}

          <div className="login-footer">
            <p>Already have an account?
              <Link to="/login"> Sign In</Link>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default UniversalRegister;
